import React, { useState, useEffect } from 'react';
import { Search, Download, User, Phone, Mail, LogIn, ChevronDown, ChevronUp, Layers, Hash, Building, Users, Target } from 'lucide-react';
import { database, ref, onValue } from '../../firebaseConfig';

const AdminLevelWiseList = () => {
  const [allMembers, setAllMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMember, setExpandedMember] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedRootSponsor, setSelectedRootSponsor] = useState('');
  const [allSponsors, setAllSponsors] = useState([]);
  const [showAllNetwork, setShowAllNetwork] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  };

  // Fetch ALL users from Firebase
  useEffect(() => {
    const usersRef = ref(database, 'users');
    
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      const members = [];
      const sponsorsSet = new Set();
      
      if (data) {
        // Iterate through date folders
        Object.keys(data).forEach(dateKey => {
          const dateData = data[dateKey];
          
          if (typeof dateData === 'object') {
            Object.keys(dateData).forEach(userKey => {
              const user = dateData[userKey];
              
              // Skip admin user
              if (userKey === 'admin' || user.userId === 'admin' || user.role === 'admin') return;
              
              // Get active package
              let packagePlan = 'No Package';
              let packageAmount = 0;
              let productStatus = 'inactive';
              
              if (user.packages && Array.isArray(user.packages)) {
                const activePackage = user.packages.find(pkg => pkg.status === 'active');
                if (activePackage) {
                  packagePlan = activePackage.packagePlan || activePackage.packageName || 'Active Package';
                  packageAmount = parseFloat(activePackage.totalAmount || activePackage.amount || activePackage.baseAmount || 0);
                  productStatus = 'active';
                }
              }

              // Get member ID
              const memberId = user.sponsorId || user.userId || userKey;
              
              // Add sponsor to set
              if (user.sponsorId) {
                sponsorsSet.add(user.sponsorId);
              }
              
              members.push({
                id: userKey,
                userId: user.userId || userKey,
                memberId: memberId,
                name: user.name || 'N/A',
                fullName: user.name || 'N/A',
                sponsorId: user.sponsorId || 'N/A',
                uplineSponsorId: user.uplineSponsorId || 'N/A',
                email: user.email || 'N/A',
                mobile: user.mobile || 'N/A',
                country: user.country || 'N/A',
                registrationDate: user.registrationDate || dateKey,
                packagePlan: packagePlan,
                packageAmount: packageAmount,
                product: packagePlan,
                productStatus: productStatus,
                status: user.status || 'active',
                balance: parseFloat(user.balance) || 0,
                deposited: parseFloat(user.deposited) || 0,
                totalEarnings: parseFloat(user.totalEarnings) || 0,
                referralCount: parseInt(user.referralCount) || 0,
                level: 0, // Will be calculated
                networkCount: 0,
                registrationDateFolder: dateKey,
                role: user.role || 'user'
              });
            });
          }
        });
      }
      
      // Convert set to array for dropdown
      const sponsorsArray = Array.from(sponsorsSet).sort();
      
      console.log('Total users found:', members.length);
      console.log('Total sponsors:', sponsorsArray.length);
      
      setAllMembers(members);
      setAllSponsors(sponsorsArray);
      
      if (showAllNetwork) {
        // Show all users without level calculation
        setFilteredMembers(members);
      }
      
      setLoading(false);
    }, (error) => {
      console.error("Error fetching data:", error);
      setLoading(false);
    });
  }, [showAllNetwork]);

  // Calculate network levels for a specific root sponsor
  const calculateNetworkForSponsor = (members, rootSponsorId) => {
    const levelWiseData = {
      level1: [],
      level2: [],
      level3: [],
      level4: [],
      level5: []
    };

    if (!members.length || !rootSponsorId) {
      console.log('No root sponsor ID provided');
      return { membersWithLevels: [], levelWiseData };
    }

    console.log('Calculating network for sponsor:', rootSponsorId);

    // Reset all levels to 0 first
    members.forEach(member => {
      member.level = 0;
    });

    // Find the root user
    const rootUser = members.find(m => m.sponsorId === rootSponsorId);
    if (!rootUser) {
      console.log('Root sponsor not found in members');
      return { membersWithLevels: [], levelWiseData };
    }

    // Level 1: Direct referrals
    const level1Members = members.filter(member => 
      member.uplineSponsorId === rootSponsorId
    );
    
    console.log('Level 1 (Direct):', level1Members.length);
    level1Members.forEach(member => {
      member.level = 1;
      levelWiseData.level1.push(member);
    });

    // Level 2: Referrals of Level 1
    const level2Members = [];
    level1Members.forEach(level1Member => {
      const downlines = members.filter(member => 
        member.uplineSponsorId === level1Member.sponsorId && member.level === 0
      );
      downlines.forEach(member => {
        member.level = 2;
        level2Members.push(member);
        levelWiseData.level2.push(member);
      });
    });

    // Level 3: Referrals of Level 2
    const level3Members = [];
    level2Members.forEach(level2Member => {
      const downlines = members.filter(member => 
        member.uplineSponsorId === level2Member.sponsorId && member.level === 0
      );
      downlines.forEach(member => {
        member.level = 3;
        level3Members.push(member);
        levelWiseData.level3.push(member);
      });
    });

    // Level 4: Referrals of Level 3
    const level4Members = [];
    level3Members.forEach(level3Member => {
      const downlines = members.filter(member => 
        member.uplineSponsorId === level3Member.sponsorId && member.level === 0
      );
      downlines.forEach(member => {
        member.level = 4;
        level4Members.push(member);
        levelWiseData.level4.push(member);
      });
    });

    // Level 5: Referrals of Level 4
    const level5Members = [];
    level4Members.forEach(level4Member => {
      const downlines = members.filter(member => 
        member.uplineSponsorId === level4Member.sponsorId && member.level === 0
      );
      downlines.forEach(member => {
        member.level = 5;
        level5Members.push(member);
        levelWiseData.level5.push(member);
      });
    });

    const membersWithLevels = members.filter(member => member.level > 0);
    
    console.log('Network calculation complete:', {
      level1: levelWiseData.level1.length,
      level2: levelWiseData.level2.length,
      level3: levelWiseData.level3.length,
      level4: levelWiseData.level4.length,
      level5: levelWiseData.level5.length,
      totalNetwork: membersWithLevels.length,
      rootSponsorId: rootSponsorId
    });

    return { membersWithLevels, levelWiseData };
  };

  // Handle sponsor selection change
  useEffect(() => {
    if (!selectedRootSponsor || showAllNetwork) {
      setFilteredMembers(allMembers);
      return;
    }

    const { membersWithLevels } = calculateNetworkForSponsor(allMembers, selectedRootSponsor);
    
    let filtered = membersWithLevels;
    
    // Filter by level if not 'all'
    if (selectedLevel !== 'all') {
      filtered = membersWithLevels.filter(member => member.level === parseInt(selectedLevel));
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(term) ||
        member.memberId.toLowerCase().includes(term) ||
        member.mobile.toLowerCase().includes(term) ||
        member.email.toLowerCase().includes(term) ||
        member.sponsorId.toLowerCase().includes(term)
      );
    }
    
    setFilteredMembers(filtered);
  }, [selectedRootSponsor, selectedLevel, searchTerm, allMembers, showAllNetwork]);

  // Handle search and level filtering
  useEffect(() => {
    if (allMembers.length === 0) {
      setFilteredMembers([]);
      return;
    }

    let filtered = allMembers;
    
    // If showing specific network
    if (!showAllNetwork && selectedRootSponsor) {
      const { membersWithLevels } = calculateNetworkForSponsor(allMembers, selectedRootSponsor);
      filtered = membersWithLevels;
    }
    
    // Filter by level if not 'all'
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(member => member.level === parseInt(selectedLevel));
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(term) ||
        member.memberId.toLowerCase().includes(term) ||
        member.mobile.toLowerCase().includes(term) ||
        member.email.toLowerCase().includes(term) ||
        member.sponsorId.toLowerCase().includes(term)
      );
    }
    
    setFilteredMembers(filtered);
  }, [searchTerm, selectedLevel, allMembers, selectedRootSponsor, showAllNetwork]);

  const getLevelStats = () => {
    let membersToCalculate = allMembers;
    
    if (!showAllNetwork && selectedRootSponsor) {
      const { membersWithLevels } = calculateNetworkForSponsor(allMembers, selectedRootSponsor);
      membersToCalculate = membersWithLevels;
    }

    const stats = {
      all: { 
        count: membersToCalculate.length, 
        label: 'All Levels', 
        color: 'bg-primary/20'
      },
      1: { 
        count: membersToCalculate.filter(m => m.level === 1).length, 
        label: 'Level 1', 
        color: 'bg-blue-500/20'
      },
      2: { 
        count: membersToCalculate.filter(m => m.level === 2).length, 
        label: 'Level 2', 
        color: 'bg-green-500/20'
      },
      3: { 
        count: membersToCalculate.filter(m => m.level === 3).length, 
        label: 'Level 3', 
        color: 'bg-purple-500/20'
      },
      4: { 
        count: membersToCalculate.filter(m => m.level === 4).length, 
        label: 'Level 4', 
        color: 'bg-yellow-500/20'
      },
      5: { 
        count: membersToCalculate.filter(m => m.level === 5).length, 
        label: 'Level 5', 
        color: 'bg-red-500/20'
      },
    };
    
    return stats;
  };

  const levelStats = getLevelStats();

  const toggleExpand = (id) => {
    setExpandedMember(prev => prev === id ? null : id);
  };

  const exportToCSV = () => {
    if (filteredMembers.length === 0) return;
    
    const headers = [
      'Sr.No', 'Level', 'Member ID', 'Name', 'Mobile', 'Email',
      'Package', 'Amount', 'Join Date', 'Sponsor ID', 'Upline ID', 'Status', 'Root Sponsor'
    ];
    
    const csvData = filteredMembers.map((member, index) => [
      index + 1,
      `L${member.level}`,
      member.memberId,
      member.name,
      member.mobile,
      member.email,
      member.packagePlan,
      member.packageAmount,
      formatDate(member.registrationDate),
      member.sponsorId,
      member.uplineSponsorId,
      member.productStatus === 'active' ? 'Active' : 'Inactive',
      selectedRootSponsor || 'All Network'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin_network_${selectedRootSponsor || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const autoLoginAsUser = (userData) => {
    localStorage.setItem('autoLoginUserId', userData.userId);
    localStorage.setItem('autoLoginUserName', userData.name);
    window.open(`/user-dashboard?autoLogin=true`, '_blank');
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 1: return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 2: return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 3: return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      case 4: return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 5: return 'bg-red-500/20 text-red-400 border border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="ml-4 text-muted-foreground">Loading all users data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Building className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin - Network Level Wise List</h1>
            <p className="text-muted-foreground mt-1">View complete network hierarchy for any sponsor</p>
          </div>
        </div>
      </div>

      {/* Network Selection */}
      <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-primary" />
              <label className="text-foreground font-medium">View Network For</label>
            </div>
            <div className="flex gap-3 mb-3">
              <button
                onClick={() => {
                  setShowAllNetwork(true);
                  setSelectedRootSponsor('');
                }}
                className={`px-4 py-2 rounded-lg transition-all ${showAllNetwork ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                All Users ({allMembers.length})
              </button>
              <button
                onClick={() => setShowAllNetwork(false)}
                className={`px-4 py-2 rounded-lg transition-all ${!showAllNetwork ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}
              >
                <Target className="h-4 w-4 inline mr-2" />
                Specific Sponsor
              </button>
            </div>
            
            {!showAllNetwork && (
              <div>
                <label className="block text-muted-foreground text-sm mb-2">Select Sponsor ID</label>
                <div className="flex gap-2">
                  <select
                    value={selectedRootSponsor}
                    onChange={(e) => setSelectedRootSponsor(e.target.value)}
                    className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary"
                  >
                    <option value="">Select a sponsor...</option>
                    {allSponsors.map(sponsorId => (
                      <option key={sponsorId} value={sponsorId}>
                        {sponsorId}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setSelectedRootSponsor('');
                      setShowAllNetwork(true);
                    }}
                    className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-4 w-4 text-primary" />
              <label className="text-foreground font-medium">Current Selection</label>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              {showAllNetwork ? (
                <div>
                  <div className="text-primary font-bold">Viewing All Users</div>
                  <div className="text-sm text-muted-foreground">{allMembers.length} total users in system</div>
                </div>
              ) : selectedRootSponsor ? (
                <div>
                  <div className="text-primary font-bold">Network for: {selectedRootSponsor}</div>
                  <div className="text-sm text-muted-foreground">
                    Showing {filteredMembers.length} members in network
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground">Please select a sponsor ID</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Level Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
        {Object.entries(levelStats).map(([level, stat]) => (
          <button
            key={level}
            onClick={() => setSelectedLevel(level === 'all' ? 'all' : parseInt(level))}
            className={`p-4 rounded-xl border transition-all hover:scale-105 ${
              (level === 'all' && selectedLevel === 'all') || 
              (level !== 'all' && selectedLevel === parseInt(level))
                ? 'border-primary bg-primary/20 shadow-lg'
                : 'border-border bg-secondary/50 hover:bg-secondary/70'
            }`}
          >
            <div className="text-center">
              <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div className="text-muted-foreground text-xs mb-1">
                {stat.label}
              </div>
              <div className="text-xl font-bold text-foreground">
                {stat.count}
              </div>
              {level !== 'all' && stat.count > 0 && levelStats.all.count > 0 && (
                <div className="text-[10px] text-green-400 mt-1">
                  {Math.round((stat.count / levelStats.all.count) * 100)}%
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* System Summary */}
      <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">System Summary</h3>
            <div className="text-muted-foreground text-sm">
              {showAllNetwork ? 'Complete system overview' : `Network overview for ${selectedRootSponsor}`}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Active Users</div>
            <div className="text-2xl font-bold text-green-400">
              {allMembers.filter(m => m.productStatus === 'active').length}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-secondary/50 p-3 rounded-lg">
            <div className="text-muted-foreground text-xs mb-1">Total Users</div>
            <div className="text-lg font-bold text-foreground">
              {allMembers.length}
            </div>
          </div>
          
          <div className="bg-secondary/50 p-3 rounded-lg">
            <div className="text-muted-foreground text-xs mb-1">Total Package Value</div>
            <div className="text-lg font-bold text-foreground">
              ${allMembers
                .filter(m => m.productStatus === 'active')
                .reduce((sum, m) => sum + m.packageAmount, 0)
                .toLocaleString()}
            </div>
          </div>
          
          <div className="bg-secondary/50 p-3 rounded-lg">
            <div className="text-muted-foreground text-xs mb-1">Total Balance</div>
            <div className="text-lg font-bold text-foreground">
              ${allMembers.reduce((sum, m) => sum + m.balance, 0).toLocaleString()}
            </div>
          </div>
          
          <div className="bg-secondary/50 p-3 rounded-lg">
            <div className="text-muted-foreground text-xs mb-1">Total Earnings</div>
            <div className="text-lg font-bold text-foreground">
              ${allMembers.reduce((sum, m) => sum + m.totalEarnings, 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Search & Export */}
      <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, member ID, mobile or email..."
                className="w-full pl-10 pr-4 py-2 bg-secondary/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredMembers.length === 0}
            className={`px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2 ${
              filteredMembers.length === 0
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'btn-electric text-primary-foreground font-semibold hover:scale-105'
            }`}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Current View Info */}
      {!showAllNetwork && selectedRootSponsor && (
        <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-primary">Network View</h3>
              <div className="text-sm text-muted-foreground">
                Showing {filteredMembers.length} members for sponsor: <span className="font-bold">{selectedRootSponsor}</span>
              </div>
            </div>
            {selectedLevel !== 'all' && (
              <div className={`px-4 py-2 rounded-full text-sm font-bold ${getLevelColor(parseInt(selectedLevel))}`}>
                <Layers className="h-4 w-4 inline mr-2" />
                Level {selectedLevel} • {filteredMembers.length} members
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="space-y-4">
        {filteredMembers.length === 0 ? (
          <div className="text-center py-12 bg-secondary/30 border border-border rounded-xl">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {showAllNetwork ? 'No Users Found' : `No Network Members Found`}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm 
                ? 'Try a different search term'
                : !showAllNetwork && !selectedRootSponsor
                ? 'Please select a sponsor ID to view network'
                : 'No data matches the current filters'}
            </p>
            
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-6 px-6 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          filteredMembers.map((member, index) => (
            <div key={member.id} className="bg-secondary/30 border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
              {/* Header */}
              <div 
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-secondary/50"
                onClick={() => toggleExpand(member.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    {!showAllNetwork && member.level > 0 && (
                      <div className="absolute -bottom-1 -right-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getLevelColor(member.level)}`}>
                          L{member.level}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground truncate">{member.name}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      <div>ID: {member.memberId}</div>
                      <div>Upline: {member.uplineSponsorId || 'Direct'}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    member.productStatus === 'active' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {member.productStatus === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {expandedMember === member.id ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedMember === member.id && (
                <div className="border-t border-border p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-3">
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Email</div>
                        <div className="text-foreground flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {member.email}
                        </div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Mobile</div>
                        <div className="text-foreground flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {member.mobile}
                        </div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Country</div>
                        <div className="text-foreground">{member.country || 'N/A'}</div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Registration Date</div>
                        <div className="text-foreground">{formatDate(member.registrationDate)}</div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Package</div>
                        <div className="text-foreground font-medium">{member.packagePlan}</div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Package Amount</div>
                        <div className="text-foreground font-medium text-lg">${member.packageAmount.toLocaleString()}</div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Sponsor ID</div>
                        <div className="text-foreground font-mono">{member.sponsorId}</div>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg">
                        <div className="text-muted-foreground text-xs mb-1">Referrals</div>
                        <div className="text-foreground font-medium">{member.referralCount}</div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <div className="text-muted-foreground text-xs mb-1">Balance</div>
                      <div className="text-foreground font-bold">${member.balance.toLocaleString()}</div>
                    </div>
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <div className="text-muted-foreground text-xs mb-1">Deposited</div>
                      <div className="text-foreground font-bold">${member.deposited.toLocaleString()}</div>
                    </div>
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <div className="text-muted-foreground text-xs mb-1">Total Earnings</div>
                      <div className="text-foreground font-bold">${member.totalEarnings.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Network Info (if viewing specific network) */}
                  {!showAllNetwork && member.level > 0 && (
                    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="text-blue-400 text-sm font-medium mb-1">Network Information</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Level:</span>
                          <span className="ml-2 font-bold">L{member.level}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Root Sponsor:</span>
                          <span className="ml-2 font-bold">{selectedRootSponsor}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => autoLoginAsUser(member)}
                      className="flex-1 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <LogIn className="h-4 w-4" />
                      Login as User
                    </button>
                    <button
                      onClick={() => {
                        const details = `
${!showAllNetwork ? `Network Level: L${member.level}` : ''}
Member: ${member.name}
Member ID: ${member.memberId}
Sponsor ID: ${member.sponsorId}
Upline ID: ${member.uplineSponsorId}
Package: ${member.packagePlan} ($${member.packageAmount})
Mobile: ${member.mobile}
Email: ${member.email}
Status: ${member.productStatus}
                        `.trim();
                        navigator.clipboard.writeText(details);
                        alert('Member details copied to clipboard!');
                      }}
                      className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors"
                    >
                      Copy Details
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer Stats */}
      {filteredMembers.length > 0 && (
        <div className="mt-6 bg-secondary/30 border border-border rounded-xl p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-foreground">{filteredMembers.length}</div>
              <div className="text-muted-foreground text-sm">
                {showAllNetwork ? 'Total Users' : 'Network Members'}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">
                {filteredMembers.filter(m => m.productStatus === 'active').length}
              </div>
              <div className="text-green-400 text-sm">Active Members</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                ${filteredMembers
                  .filter(m => m.productStatus === 'active')
                  .reduce((sum, m) => sum + m.packageAmount, 0)
                  .toLocaleString()}
              </div>
              <div className="text-muted-foreground text-sm">Active Package Value</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {filteredMembers.reduce((sum, m) => sum + m.referralCount, 0)}
              </div>
              <div className="text-muted-foreground text-sm">Total Referrals</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLevelWiseList;